import {
  Badge,
  Box,
  Card,
  Center,
  Flex,
  Group,
  Stack,
  Table,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { capitalize, upperCase } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "../../UserProfile.module.css";

import {
  EmptyStateRevamped,
  ListTable,
  PlanSwitcherCard,
  TextSerif,
  useColumnSorter,
} from "@/components/elements";
import { plansPrices } from "@/components/elements/PlansCard/functions/const";
import { ChoosePlanModal } from "@/components/widgets/PlanModals";
import { currencyMap, defaultCurrency } from "@/lib/currency";
import { useAllPayments } from "@/services/payments/get-all-payments";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { Currency } from "@/types/currency";
import { formatShortDate } from "@/utils/formatDate";

import type { PaymentData } from "@/types/payments";
import type { FC, ReactNode } from "react";

export const UserProfileTabBilling: FC = () => {
  const { t } = useTranslation();
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();
  const { data: plans, isLoading: isPlansLoading } = usePlansUser();
  const { data: billingData = [], isLoading: isBillingLoading } = useAllPayments();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const [page, setPage] = useState(1);
  const pageSize = 5;

  const columnsDataTable: {
    id: keyof Partial<PaymentData> | "billingCycle" | "description";
    label: string;
  }[] = [
    { id: "billingCycle", label: t("payments.billingCycle") },
    { id: "status", label: t("payments.status") },
    { id: "description", label: t("payments.description") },
    { id: "amount", label: t("payments.amount") },
  ];

  function sortDataBy(
    id: keyof Partial<PaymentData> | "billingCycle",
    sortBy: "asc" | "desc" = "asc"
  ) {
    return (a: Partial<PaymentData>, b: Partial<PaymentData>) => {
      if (id === "billingCycle") {
        const aValue = a.period_start;
        const bValue = b.period_start;

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortBy === "asc" ? -1 : 1;
        if (bValue == null) return sortBy === "asc" ? 1 : -1;

        const aTime = new Date(aValue).getTime();
        const bTime = new Date(bValue).getTime();

        return sortBy === "asc" ? aTime - bTime : bTime - aTime;
      }

      const aValue = a[id];
      const bValue = b[id];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortBy === "asc" ? -1 : 1;
      if (bValue == null) return sortBy === "asc" ? 1 : -1;

      if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
        return sortBy === "asc" ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
      }

      return sortBy === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    };
  }

  const { data, getSortById, getUpdateSort } = useColumnSorter<Partial<PaymentData>>(
    "user-billing-table-sort",
    { id: "period_start", sortBy: "desc" },
    { data: billingData, sortBy: sortDataBy }
  );

  const paginatedData = data.slice((page - 1) * pageSize, page * pageSize);

  function getColumnSorter(id: keyof Partial<PaymentData> | "period_start") {
    return (
      <ListTable.ColumnSorter
        sortBy={getSortById(id)}
        onSortChange={(sort) => getUpdateSort(id)(sort)}
      />
    );
  }

  const isProfessional = plans?.name === "professional" || false;

  // Use user's preferred currency from local storage (already read into `currency`)
  const planCurrency = (currency?.code ?? defaultCurrency.code).toString().toUpperCase();

  // Pick the corresponding monthly price explicitly and show symbol
  const planPrice = plans?.name
    ? (() => {
        const priceObj = plansPrices[plans.name] ?? { monthlyEUR: "", monthlyUSD: "" };
        const value =
          planCurrency === "USD"
            ? (priceObj.monthlyUSD ?? priceObj.monthlyEUR)
            : (priceObj.monthlyEUR ?? priceObj.monthlyUSD);
        const symbol = (currencyMap[planCurrency]?.symbol as string) ?? planCurrency;
        return `${value}${symbol}/month`;
      })()
    : "";

  return (
    <Stack gap="lg">
      <PlanSwitcherCard
        titleCard={
          isProfessional
            ? t("plans.planSwitcherCard.professionalPlanTitle")
            : t("plans.planSwitcherCard.upgradeTitle")
        }
        descriptionCard={
          isProfessional
            ? t("plans.planSwitcherCard.professionalPlanDescription")
            : t("plans.planSwitcherCard.upgradeDescription")
        }
        buttonText={
          isProfessional
            ? t("plans.planSwitcherCard.viewPlan")
            : t("plans.planSwitcherCard.upgradeText")
        }
        onClick={openPlanModal}
        isLoading={isPlansLoading || isBillingLoading}
      />

      <ChoosePlanModal
        isOpen={showPlanModal}
        onClose={() => closePlanModal()}
        title={t("plans.changePlanAnytime")}
      />

      <Card>
        <Box p="md">
          <Group justify="space-between" align="center">
            <Title order={3} fw="normal" fz="1.25em" pb="md">
              {t("payments.plan_and_billing")}
            </Title>
          </Group>

          <Stack gap="xs" style={{ flex: 1 }}>
            {[
              { label: t("payments.yourPlan"), value: plans?.name },
              { label: t("payments.pricing"), value: planPrice },
              { label: t("payments.subscriptionDate"), value: formatShortDate(plans?.created_at) },
            ].map((field) => (
              <Box key={field.label} py="md">
                <Group justify="space-between" align="center" w="100%">
                  <TextSerif size="sm">{field.label}</TextSerif>
                  <Text size="md">{capitalize(field.value)}</Text>
                </Group>
                <Box mt="xs" className={classes.userData_list} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Card>

      <Card>
        <Box p="md">
          <Title order={3} fw="normal" fz="1.25em" pb="lg">
            {t("payments.billingHistory")}
          </Title>

          {billingData.length > 0 && (
            <Flex justify="space-between" align="center" mb="sm" w={250}>
              <Text c="gray">{t("payments.invoice")}</Text>
              <Text fw="bold">
                {billingData.length} {t("payments.invoices")}
              </Text>
            </Flex>
          )}
        </Box>

        <Stack p="md">
          {billingData.length === 0 ? (
            <Center h="10em">
              <EmptyStateRevamped
                title={t("payments.billingHistory")}
                description={t("payments.noPayments")}
              />
            </Center>
          ) : (
            <>
              <ListTable
                data={{
                  head: columnsDataTable.map((col) => ({
                    ...col,
                    sorter:
                      col.id !== "description"
                        ? getColumnSorter(col.id as keyof PaymentData | "period_start")
                        : undefined,
                  })),
                  body: paginatedData.map((row, index) => (
                    <Table.Tr key={row.id ?? index} style={{ cursor: "pointer" }}>
                      {columnsDataTable.map((col) => (
                        <Table.Td key={`${row.id ?? index}_${col.id}`}>
                          {col.id === "billingCycle" ? (
                            <>
                              {row.period_start ? formatShortDate(row.period_start) : ""} {" - "}
                              {row.period_end ? formatShortDate(row.period_end) : ""}
                            </>
                          ) : col.id === "description" ? (
                            row.period_start ? (
                              new Date(row.period_start).toLocaleString(undefined, {
                                month: "long",
                                year: "numeric",
                              })
                            ) : (
                              ""
                            )
                          ) : col.id === "status" ? (
                            <Badge
                              color={
                                row.status === "paid"
                                  ? isDark
                                    ? "#23ad4e"
                                    : "var(--mantine-color-success-7)"
                                  : isDark
                                    ? "#ff8686"
                                    : "red"
                              }
                              variant="light"
                            >
                              {upperCase(row.status)}
                            </Badge>
                          ) : col.id === "amount" ? (
                            <>
                              {row[col.id] as ReactNode}{" "}
                              {row.currency
                                ? (currencyMap[row.currency.toUpperCase()]?.symbol ?? row.currency)
                                : ""}
                            </>
                          ) : (
                            (row[col.id as keyof PaymentData] as ReactNode)
                          )}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  )),
                }}
                tableProps={{ highlightOnHover: true }}
              />

              {billingData.length > pageSize && (
                <Flex justify="end">
                  <ListTable.Pagination
                    page={page}
                    pageSize={pageSize}
                    total={billingData.length}
                    onChange={setPage}
                  />
                </Flex>
              )}
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
